// 📄 src/pages/AutoReplyBuilder/components/AutoReplyFlowsListModal.jsx
import React, { useCallback, useEffect, useState } from "react";
import axiosClient from "../../../api/axiosClient";
import { X, RefreshCcw, FlaskConical, Trash2, Copy } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../app/providers/AuthProvider";

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function formatSenderDisplayLabel(number) {
  if (!number) return "";
  const phone = String(
    number.whatsAppBusinessNumber ||
      number.displayPhoneNumber ||
      number.phoneNumber ||
      number.phoneNumberId ||
      ""
  ).trim();
  const name = String(
    number.senderDisplayName || number.displayName || number.name || ""
  ).trim();

  if (phone && name) return `${phone} (${name})`;
  return phone || name || "";
}

export default function AutoReplyFlowsListModal({
  open,
  onClose,
  onFlowSelected,
  onFlowDeleted,
  onActivationValidationFailed,
}) {
  const [flows, setFlows] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  // 🔐 get business id for test-match
  const { business } = useAuth() || {};
  const businessIdFromAuth = business?.id;

  // 🔬 test-match modal state
  const [testOpen, setTestOpen] = useState(false);
  const [testFlow, setTestFlow] = useState(null);
  const [testText, setTestText] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneFlow, setCloneFlow] = useState(null);
  const [cloneName, setCloneName] = useState("");
  const [copyRecoverySettings, setCopyRecoverySettings] = useState(true);
  const [cloneLoading, setCloneLoading] = useState(false);

  const loadFlows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosClient.get("autoreplyflows");
      setFlows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load flows list", err);
      setError(
        err?.response?.data?.message || err?.message || "Unable to load flows."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // load flows when modal opens
  useEffect(() => {
    if (!open) return;
    loadFlows();
    axiosClient.get("/whatsappsettings/META_CLOUD/numbers")
      .then(({ data }) => setPhoneNumbers(Array.isArray(data) ? data : []))
      .catch(() => setPhoneNumbers([]));
  }, [open, loadFlows]);

  // toggle active / inactive status for a single flow
  const handleToggleStatus = async (flow, evt) => {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }

    const next = !flow.isActive;
    if (statusUpdatingId === flow.id) return;

    setStatusUpdatingId(flow.id);
    try {
      await axiosClient.patch(`autoreplyflows/${flow.id}/status`, {
        isActive: next,
      });

      setFlows(prev =>
        prev.map(f =>
          f.id === flow.id
            ? {
                ...f,
                isActive: next,
                isDraft: false,
                updatedAt: new Date().toISOString(),
              }
            : f
        )
      );

      toast.success(
        next ? "Flow activated successfully." : "Flow deactivated successfully."
      );
      await loadFlows();
    } catch (err) {
      console.error("Failed to toggle flow status", err);
      const status = err?.response?.status;
      const response = err?.response?.data;
      const payload = response?.payload || response?.data || {};
      const code = payload?.code || response?.code || null;
      const message =
        response?.message ||
        response?.Message ||
        payload?.message ||
        "Failed to update flow status. Please try again.";
      const validationFailure =
        next &&
        (status === 400 || status === 409) &&
        Boolean(code || response?.message || payload?.message);

      if (validationFailure && typeof onActivationValidationFailed === "function") {
        onActivationValidationFailed({
          flowId: flow.id,
          code,
          message,
          nodeId: payload?.nodeId || response?.nodeId || null,
          details: payload?.details || response?.details || null,
        });
      } else {
        toast.error(message);
      }
    } finally {
      setStatusUpdatingId(null);
    }
  };

  // 🔬 open test modal for a given flow
  const openTestForFlow = (flow, evt) => {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }

    setTestFlow(flow);
    setTestResult(null);
    setTestError(null);

    const raw = flow.triggerKeyword || "";
    const firstKeyword =
      raw
        .split(/[,|\n|\r]/)
        .map(x => x.trim())
        .filter(Boolean)[0] || "";
    setTestText(firstKeyword);

    setTestOpen(true);
  };

  const closeTestModal = () => {
    setTestOpen(false);
    setTestFlow(null);
    setTestText("");
    setTestResult(null);
    setTestError(null);
    setTestLoading(false);
  };

  const handleRunTest = async () => {
    if (!testText.trim()) return;

    let businessIdFromStorage = null;
    try {
      businessIdFromStorage = localStorage.getItem("sa_selectedBusinessId");
    } catch {
      businessIdFromStorage = null;
    }

    const effectiveBusinessId =
      (typeof businessIdFromAuth === "string" && businessIdFromAuth.trim()) ||
      (typeof businessIdFromStorage === "string" &&
        businessIdFromStorage.trim()) ||
      null;

    if (!effectiveBusinessId) {
      const msg = "Please select a business first (SuperAdmin).";
      setTestError(msg);
      toast.error(msg);
      return;
    }

    if (!testFlow?.phoneNumberId) {
      const msg = "Select a scoped flow to run a phone-specific match preview.";
      setTestError(msg);
      toast.error(msg);
      return;
    }

    setTestLoading(true);
    setTestError(null);
    setTestResult(null);

    try {
      const payload = {
        businessId: effectiveBusinessId,
        phoneNumberId: testFlow.phoneNumberId,
        incomingText: testText.trim(),
      };

      const res = await axiosClient.post("autoreplyflows/test-match", payload);
      setTestResult(res?.data || null);
    } catch (err) {
      console.error("Test-match error (flows modal)", err);
      setTestError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to test auto-reply match."
      );
    } finally {
      setTestLoading(false);
    }
  };

  const handleDeleteFlow = async (flow, evt) => {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }

    const confirmDelete = window.confirm(
      "Delete this flow? Used flows will be archived automatically to preserve history."
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const res = await axiosClient.delete(`autoreplyflows/${flow.id}`);
      const action = res?.data?.payload?.action || res?.data?.data?.action || null;

      if (action === "ARCHIVED") {
        toast.info("Flow archived to preserve history and active session safety.");
      } else {
        toast.success("Flow deleted permanently.");
      }

      onFlowDeleted?.(flow.id, action);
      await loadFlows();
    } catch (err) {
      console.error("Failed to delete/archive flow", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to delete flow. Please try again."
      );
    }
  };

  const openCloneForFlow = (flow, evt) => {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }

    setCloneFlow(flow);
    setCloneName(`${flow?.name || "AutoReply Flow"} - Clone`);
    setCopyRecoverySettings(true);
    setCloneLoading(false);
    setCloneOpen(true);
  };

  const closeCloneModal = () => {
    if (cloneLoading) return;
    setCloneOpen(false);
    setCloneFlow(null);
    setCloneName("");
    setCopyRecoverySettings(true);
  };

  const handleCloneFlow = async () => {
    if (cloneLoading || !cloneFlow?.id) return;

    const nextName = cloneName.trim();
    if (!nextName) {
      toast.error("Flow name is required.");
      return;
    }

    setCloneLoading(true);
    try {
      const { data } = await axiosClient.post(
        `autoreplyflows/${cloneFlow.id}/clone`,
        {
          name: nextName,
          copyRecoverySettings,
        }
      );

      toast.success("Flow cloned successfully");
      await loadFlows();
      const clonedFlowId = data?.id || data?.Id;
      if (clonedFlowId && typeof onFlowSelected === "function") {
        onFlowSelected(clonedFlowId);
      }
      setCloneOpen(false);
      setCloneFlow(null);
      setCloneName("");
      setCopyRecoverySettings(true);
      onClose?.();
    } catch (err) {
      console.error("Failed to clone flow", err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.errorMessage ||
          "Failed to clone flow. Please try again."
      );
    } finally {
      setCloneLoading(false);
    }
  };

  if (!open) return null;

  const isThisFlowSelected =
    testResult &&
    testResult.isMatch &&
    testFlow &&
    testResult.flowId === testFlow.id;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50">
          <div>
            <h2 className="text-base font-semibold text-emerald-900">
              Auto-Reply Flows
            </h2>
            <p className="text-xs text-emerald-700">
              Use the toggle to activate or deactivate a flow. Activation
              validates the flow; issues will open the flow on the canvas. Use{" "}
              <span className="font-semibold">Test</span> to see which flow
              would trigger for a sample message.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadFlows}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs border border-emerald-200 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-full p-1.5 hover:bg-emerald-100"
            >
              <X className="w-4 h-4 text-emerald-700" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto bg-white">
          {loading && (
            <div className="p-4 text-sm text-slate-600 flex items-center gap-2">
              <RefreshCcw className="w-4 h-4 animate-spin" />
              Loading flows...
            </div>
          )}

          {error && !loading && (
            <div className="p-4 text-sm text-red-600">{error}</div>
          )}

          {!loading && !error && flows.length === 0 && (
            <div className="p-4 text-sm text-slate-500">
              No flows found yet. Create your first auto-reply flow from the
              canvas.
            </div>
          )}

          {!loading && !error && flows.length > 0 && (
            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
              {flows.map(flow => {
                const active = flow.isActive;
                const draft = flow.isDraft === true;
                const keywords = (flow.triggerKeyword || "")
                  .split(/[,|\n|\r]/)
                  .map(k => k.trim())
                  .filter(Boolean);
                const sender = phoneNumbers.find(number => number.phoneNumberId === flow.phoneNumberId);
                return (
                  <div
                    key={flow.id}
                    onClick={() => {
                      if (typeof onFlowSelected === "function") onFlowSelected(flow.id);
                      onClose?.();
                    }}
                    className="group relative flex cursor-pointer flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-[1px] hover:border-emerald-300 hover:shadow-md"
                  >
                    {/* Card header: name + status badge */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-[13px] font-semibold text-slate-800">{flow.name}</p>
                        <p className="mt-0.5 text-[10px] text-slate-400">{formatDate(flow.updatedAt)}</p>
                        <p className="mt-0.5 truncate text-[10px] text-slate-500">
                          {flow.phoneNumberId
                            ? `WhatsApp: ${formatSenderDisplayLabel(sender) || flow.phoneNumberId}`
                            : "All Number"}
                        </p>
                      </div>
                      <span
                        className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          draft
                            ? "border border-amber-100 bg-amber-50 text-amber-700"
                            : active
                            ? "border border-emerald-100 bg-emerald-50 text-emerald-700"
                            : "border border-slate-200 bg-slate-100 text-slate-500"
                        }`}
                      >
                        <span className={`mr-1 h-1.5 w-1.5 rounded-full ${
                          draft
                            ? "bg-amber-500"
                            : active
                            ? "bg-emerald-500"
                            : "bg-slate-400"
                        }`} />
                        {draft ? "Draft" : active ? "Active" : "Inactive"}
                      </span>
                      {flow.isDefaultEntryFlow && (
                        <span className="ml-1 inline-flex shrink-0 rounded-full border border-violet-100 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">Default</span>
                      )}
                    </div>

                    {/* Keyword chips */}
                    {keywords.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {keywords.slice(0, 4).map((kw, i) => (
                          <span
                            key={i}
                            className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600"
                          >
                            {kw}
                          </span>
                        ))}
                        {keywords.length > 4 && (
                          <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-400">
                            +{keywords.length - 4} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Action row */}
                    <div className="flex items-center justify-between border-t border-slate-100 pt-2.5">
                      <button
                        type="button"
                        onClick={evt => openTestForFlow(flow, evt)}
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-200 px-2 py-1 text-[11px] text-emerald-700 transition-colors hover:bg-emerald-50"
                      >
                        <FlaskConical className="h-3 w-3" />
                        Test
                      </button>
                      <button
                        type="button"
                        onClick={evt => openCloneForFlow(flow, evt)}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <Copy className="h-3 w-3" />
                        Clone
                      </button>
                      <button
                        type="button"
                        onClick={evt => handleToggleStatus(flow, evt)}
                        disabled={statusUpdatingId === flow.id}
                        aria-label={`${active ? "Deactivate" : "Activate"} ${flow.name}`}
                        title={`${active ? "Deactivate" : "Activate"} ${flow.name}`}
                        className={`relative inline-flex h-5 w-9 cursor-pointer items-center rounded-full border transition-colors duration-200 ${
                          active ? "border-emerald-500 bg-emerald-500" : "border-slate-300 bg-slate-300"
                        } disabled:cursor-wait disabled:opacity-60`}
                      >
                        <span
                          className={`h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${
                            active ? "translate-x-4" : "translate-x-0"
                          }`}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={evt => handleDeleteFlow(flow, evt)}
                        className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-[11px] text-red-700 transition-colors hover:bg-red-50"
                        title="Delete flow (archives automatically when history exists)"
                      >
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t bg-white flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>

      {/* 🔬 Test Auto-Reply Match modal (on top of flows modal) */}
      {testOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800">
                Test Auto-Reply Match
              </h3>
              <button
                type="button"
                onClick={closeTestModal}
                className="p-1 rounded-full hover:bg-slate-100"
              >
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            <p className="text-xs text-slate-600 mb-3">
              Flow:{" "}
              <span className="font-semibold">
                {testFlow?.name || testFlow?.id || "-"}
              </span>
            </p>

            <div className="mb-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Sample incoming message
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                placeholder="Type what the customer might send..."
                value={testText}
                onChange={e => {
                  setTestText(e.target.value);
                  setTestResult(null);
                  setTestError(null);
                }}
              />
            </div>

            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={handleRunTest}
                disabled={testLoading || !testText.trim()}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white disabled:opacity-50"
              >
                {testLoading ? (
                  <RefreshCcw className="w-4 h-4 animate-spin" />
                ) : (
                  <FlaskConical className="w-4 h-4" />
                )}
                {testLoading ? "Testing..." : "Run test"}
              </button>
            </div>

            {testError && (
              <p className="text-xs text-red-600 mb-2">{testError}</p>
            )}

            {testResult && (
              <div className="mt-2 text-xs text-slate-700 space-y-1 border-t border-slate-100 pt-2">
                <p>
                  <span className="font-semibold">Matched:</span>{" "}
                  {testResult.isMatch ? "Yes" : "No"}
                </p>

                {testResult.isMatch && (
                  <>
                    <p>
                      <span className="font-semibold">Matched flow:</span>{" "}
                      {testResult.flowName || testResult.flowId}
                    </p>
                    <p>
                      <span className="font-semibold">Is this flow?</span>{" "}
                      {isThisFlowSelected ? (
                        <span className="text-emerald-700 font-semibold">
                          Yes, this flow will trigger.
                        </span>
                      ) : (
                        <span className="text-amber-700">
                          No, a different flow has higher priority or a better
                          match.
                        </span>
                      )}
                    </p>
                    <p>
                      <span className="font-semibold">Keyword:</span>{" "}
                      {testResult.matchedKeyword || "-"}
                    </p>
                    <p>
                      <span className="font-semibold">Start node:</span>{" "}
                      {testResult.startNodeType || "?"} /{" "}
                      {testResult.startNodeName || "-"}
                    </p>
                  </>
                )}

                {!testResult.isMatch && (
                  <p className="text-slate-600">
                    No matching flow found for this input.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {cloneOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/20">
          <div
            className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-label="Clone flow"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  Clone flow
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  Original flow:{" "}
                  <span className="font-medium text-slate-700">
                    {cloneFlow?.name || "-"}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={closeCloneModal}
                disabled={cloneLoading}
                className="rounded-full p-1 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close clone flow"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  New flow name
                </label>
                <input
                  type="text"
                  value={cloneName}
                  onChange={event => setCloneName(event.target.value)}
                  disabled={cloneLoading}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:bg-slate-50"
                  autoFocus
                />
              </div>

              <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={copyRecoverySettings}
                  onChange={event => setCopyRecoverySettings(event.target.checked)}
                  disabled={cloneLoading}
                  className="h-4 w-4 accent-emerald-600"
                />
                Copy recovery settings
              </label>

              <p className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
                The cloned flow will be created as inactive. Review and activate it after making changes.
              </p>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCloneModal}
                disabled={cloneLoading}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCloneFlow}
                disabled={cloneLoading || !cloneName.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {cloneLoading && <RefreshCcw className="h-4 w-4 animate-spin" />}
                {cloneLoading ? "Cloning..." : "Clone Flow"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
